<script setup lang="ts">
import { computed } from 'vue';
import { Capacitor } from '@capacitor/core';
import { isPlatform, loadingController } from '@ionic/vue';
import { logoGoogle, logoFacebook, logoApple, callOutline, mailOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import CustomButton from '@/shared/components/CustomButton.vue';
import useTenant from '@/shared/composables/useTenant';
import useAuth from '../composables/useAuth';
import { SocialProvider } from '@/shared/interfaces/user';
import router from '@/router';

const props = defineProps<{
	previousLogin: { provider: string; timestamp: string } | null;
}>();

const emit = defineEmits<{
	(e: 'continue-with-previous', provider: string): void;
	(e: 'use-other'): void;
	(e: 'onLoginSuccess'): void;
}>();

const { t } = useI18n();

const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig, tenantStyles } = getTenantConfigAndStyles();

const { oAuthLogin } = useAuth();
const login = async (provider: SocialProvider) => {
	if (tenantStyles.value) {
		const loading = await loadingController.create({
			message: t('general.please_wait'),
		});
		await loading.present();
		try {
			const user = await oAuthLogin(provider, tenantStyles.value);
			if (user.value.token) {
				emit('onLoginSuccess');
			}
			await loading.dismiss();
		} catch (e) {
			await loading.dismiss();
		}
	}
};

const withModalClose = async (callback: () => Promise<void> | void) => {
	emit('use-other');
	await callback();
};

const handleLoginWithPrevious = () => {
	withModalClose(() => {
		if (props.previousLogin) {
			if (props.previousLogin.provider === 'phone' || props.previousLogin.provider === 'email') {
				emit('continue-with-previous', props.previousLogin.provider);
			} else if (['google', 'facebook', 'apple'].includes(props.previousLogin.provider)) {
				login(props.previousLogin.provider as SocialProvider);
			}
		}
	});
};

const goTo = async (link: string) => {
	await router.push(link);
};

const methodLabel = computed(() => {
	switch (props.previousLogin?.provider) {
		case 'google':
			return 'Google';
		case 'facebook':
			return 'Facebook';
		case 'apple':
			return 'Apple';
		case 'phone':
			return 'Phone';
		case 'email':
			return 'Email';
		default:
			return '';
	}
});
</script>

<template>
	<div v-if="previousLogin && tenantConfig && tenantStyles" class="ion-padding">
		<h1 class="text-2xl font-bold mb-2">{{ t('auth.previous_login_method.title') }}</h1>
		<p class="text-sm text-gray-400 mb-6">
			{{
				t('auth.previous_login_method.message', {
					method: methodLabel,
				})
			}}
		</p>

		<div v-if="previousLogin.provider === 'phone'" class="flex justify-center mb-2">
			<CustomButton class="ion-no-margin" fill="outline" color="anykrowd-dark" @on-click="() => withModalClose(() => goTo('/auth/signinwithphone'))" text="auth.continuePhone" :iconLeft="callOutline" />
		</div>
		<div v-else-if="previousLogin.provider === 'email'" class="flex justify-center mb-2">
			<CustomButton class="ion-no-margin" fill="outline" color="anykrowd-dark" @on-click="() => withModalClose(() => goTo('/auth/signinhome?previousLogin=true'))" text="auth.continueEmail" :iconLeft="mailOutline" />
		</div>

		<div v-if="previousLogin.provider === 'apple' && Capacitor.isNativePlatform() && isPlatform('ios')" class="flex justify-center mb-2">
			<CustomButton class="ion-no-margin" fill="outline" color="anykrowd-dark" @on-click="handleLoginWithPrevious" text="auth.continueApple" :iconLeft="logoApple" />
		</div>
		<div v-else-if="previousLogin.provider === 'facebook'" class="flex justify-center mb-2">
			<CustomButton class="ion-no-margin" fill="outline" color="anykrowd-dark" @on-click="handleLoginWithPrevious" text="auth.continueFacebook" :iconLeft="logoFacebook" />
		</div>
		<div v-else-if="previousLogin.provider === 'google'" class="flex justify-center mb-2">
			<CustomButton class="ion-no-margin" fill="outline" color="anykrowd-dark" @on-click="handleLoginWithPrevious" text="auth.continueGoogle" :iconLeft="logoGoogle" />
		</div>

		<div class="text-sm underline text-white mt-4" @click="$emit('use-other')">
			<CustomButton class="ion-no-margin" fill="outline" color="anykrowd-dark" @on-click="$emit('use-other')" text="auth.previous_login_method.use_another" />
		</div>
	</div>
</template>

<style scoped>
h1 {
	color: black;
}

p {
	color: var(--ion-color-medium);
}
</style>
